import type { CellAddress, CellRange, FillValue, Rect } from '@stageplottter/core';
import {
  cellRange,
  clampRect,
  extendRange,
  fill,
  moveCell,
  normalizeRange,
  parseTsv,
  toTsv,
} from '@stageplottter/core';
import type { FillDir, GridAction } from './keys';
import type { ColumnType, GridEdit, GridValue } from './types';
import { clearedValue, sameValue } from './values';

/**
 * Pure grid operations on visible row/column indices. The component supplies a {@link GridAccess}
 * view of its (sorted, filtered, grouped) rows; results are cell changes keyed by row key.
 */
export interface GridAccess {
  rows: number;
  cols: number;
  rowKey(row: number): string;
  columnKey(col: number): string;
  type(col: number): ColumnType;
  value(row: number, col: number): GridValue;
  text(row: number, col: number): string;
  /** `row >= rows` asks about a row that would be appended. */
  editable(row: number, col: number): boolean;
  parse(row: number, col: number, text: string): GridValue | undefined;
}

/** New selection after a navigation action, or undefined if the action does not navigate. */
export function navigate(
  range: CellRange,
  action: GridAction,
  rows: number,
  cols: number,
  pageSize = 10,
): CellRange | undefined {
  const f = range.focus;
  const to = (cell: CellAddress, extend: boolean): CellRange =>
    extend ? extendRange(range, cell) : cellRange(cell);
  switch (action.type) {
    case 'move':
      return to(moveCell(f, action.dRow, action.dCol, rows, cols), action.extend);
    case 'edge':
      return to(moveCell(f, action.dRow * rows, action.dCol * cols, rows, cols), action.extend);
    case 'home':
      return to({ row: action.grid ? 0 : f.row, col: 0 }, action.extend);
    case 'end':
      return to(
        { row: action.grid ? Math.max(rows - 1, 0) : f.row, col: Math.max(cols - 1, 0) },
        action.extend,
      );
    case 'page':
      return to(moveCell(f, action.dir * pageSize, 0, rows, cols), action.extend);
    default:
      return undefined;
  }
}

/** Tab / Shift+Tab: next cell in reading order; null when leaving the grid. */
export function tabTarget(
  cell: CellAddress,
  back: boolean,
  rows: number,
  cols: number,
): CellAddress | null {
  const index = cell.row * cols + cell.col + (back ? -1 : 1);
  if (index < 0 || index >= rows * cols) return null;
  return { row: Math.floor(index / cols), col: index % cols };
}

export function selectAll(rows: number, cols: number): CellRange {
  return {
    anchor: { row: 0, col: 0 },
    focus: { row: Math.max(rows - 1, 0), col: Math.max(cols - 1, 0) },
  };
}

export function selectionRect(
  range: CellRange | null,
  rows: number,
  cols: number,
): Rect | undefined {
  return range === null ? undefined : clampRect(normalizeRange(range), rows, cols);
}

/** Selected cells as TSV (display text, Excel/Sheets compatible). */
export function copyTsv(g: GridAccess, rect: Rect): string {
  const out: string[][] = [];
  for (let r = rect.top; r <= rect.bottom; r++) {
    const line: string[] = [];
    for (let c = rect.left; c <= rect.right; c++) line.push(g.text(r, c));
    out.push(line);
  }
  return toTsv(out);
}

function push(edit: GridEdit, g: GridAccess, row: number, col: number, value: GridValue): void {
  if (row < g.rows) {
    if (!sameValue(g.value(row, col), value))
      edit.changes.push({ rowKey: g.rowKey(row), column: g.columnKey(col), value });
    return;
  }
  const i = row - g.rows;
  while (edit.appended.length <= i) edit.appended.push({});
  edit.appended[i]![g.columnKey(col)] = value;
}

export interface PastePlan {
  edit: GridEdit;
  /** Range covering the pasted block (for the new selection). */
  range: CellRange;
}

/**
 * Pastes a TSV block at the selection. A single clipboard cell fills the whole selection;
 * otherwise the block starts at the selection's top-left cell. Rows beyond the end become
 * appended rows when `allowAppend`, else they are dropped; columns beyond the end are dropped.
 * Read-only cells and unparseable values are skipped.
 */
export function planPaste(
  g: GridAccess,
  rect: Rect,
  text: string,
  allowAppend: boolean,
): PastePlan {
  const cells = parseTsv(text);
  const edit: GridEdit = { changes: [], appended: [] };
  const height = cells.length;
  const width = Math.max(0, ...cells.map((r) => r.length));
  if (height === 0 || width === 0)
    return {
      edit,
      range: {
        anchor: { row: rect.top, col: rect.left },
        focus: { row: rect.bottom, col: rect.right },
      },
    };
  const single = height === 1 && width === 1;
  const bottom = single ? rect.bottom : rect.top + height - 1;
  const right = single ? rect.right : Math.min(rect.left + width - 1, g.cols - 1);
  const lastRow = allowAppend ? bottom : Math.min(bottom, g.rows - 1);
  for (let r = rect.top; r <= lastRow; r++) {
    for (let c = rect.left; c <= right; c++) {
      const raw = single ? cells[0]![0]! : cells[r - rect.top]![c - rect.left];
      if (raw === undefined || !g.editable(r, c)) continue;
      const value = g.parse(r, c, raw);
      if (value === undefined) continue;
      push(edit, g, r, c, value);
    }
  }
  return {
    edit,
    range: {
      anchor: { row: rect.top, col: rect.left },
      focus: { row: Math.max(rect.top, lastRow), col: right },
    },
  };
}

const LIST_PREFIX = '\u0000list:';

function toFill(v: GridValue): FillValue {
  return Array.isArray(v) ? `${LIST_PREFIX}${JSON.stringify(v)}` : (v as FillValue);
}

function fromFill(v: FillValue): GridValue {
  if (typeof v === 'string' && v.startsWith(LIST_PREFIX))
    return JSON.parse(v.slice(LIST_PREFIX.length)) as string[];
  return v ?? '';
}

/**
 * Excel fill (Ctrl+D / Ctrl+R and the context menu). A one-row selection filled down copies the
 * row above (likewise for the other directions). Series (1, 2 → 3, 4) continue as in core `fill`.
 */
export function planFill(g: GridAccess, rect: Rect, dir: FillDir): GridEdit {
  const r = { ...rect };
  if (r.top === r.bottom) {
    if (dir === 'down' && r.top > 0) r.top -= 1;
    if (dir === 'up' && r.bottom < g.rows - 1) r.bottom += 1;
  }
  if (r.left === r.right) {
    if (dir === 'right' && r.left > 0) r.left -= 1;
    if (dir === 'left' && r.right < g.cols - 1) r.right += 1;
  }
  const matrix: FillValue[][] = [];
  for (let row = r.top; row <= r.bottom; row++) {
    const line: FillValue[] = [];
    for (let col = r.left; col <= r.right; col++) line.push(toFill(g.value(row, col)));
    matrix.push(line);
  }
  const filled = fill(
    matrix,
    { anchor: { row: 0, col: 0 }, focus: { row: matrix.length - 1, col: r.right - r.left } },
    dir,
  );
  const edit: GridEdit = { changes: [], appended: [] };
  for (let row = r.top; row <= r.bottom; row++) {
    for (let col = r.left; col <= r.right; col++) {
      if (!g.editable(row, col)) continue;
      const value = coerce(g.type(col), fromFill(filled[row - r.top]![col - r.left]!));
      if (value !== undefined) push(edit, g, row, col, value);
    }
  }
  return edit;
}

/** Keeps a filled value compatible with the target column type. */
function coerce(type: ColumnType, v: GridValue): GridValue | undefined {
  if (type === 'number')
    return typeof v === 'number' || v === ''
      ? v
      : Number.isFinite(Number(v))
        ? Number(v)
        : undefined;
  if (type === 'boolean') return typeof v === 'boolean' ? v : undefined;
  return v;
}

/** Delete / Backspace on the selection. */
export function planClear(g: GridAccess, rect: Rect): GridEdit {
  const edit: GridEdit = { changes: [], appended: [] };
  for (let row = rect.top; row <= rect.bottom; row++) {
    for (let col = rect.left; col <= rect.right; col++) {
      if (g.editable(row, col)) push(edit, g, row, col, clearedValue(g.type(col)));
    }
  }
  return edit;
}

/** One value for a single cell (editor commit / toggle). */
export function planSet(g: GridAccess, row: number, col: number, value: GridValue): GridEdit {
  const edit: GridEdit = { changes: [], appended: [] };
  // An empty value on the "new row" line does not create a row.
  if (row >= g.rows && sameValue(value, clearedValue(g.type(col)))) return edit;
  if (g.editable(row, col)) push(edit, g, row, col, value);
  return edit;
}

/** Row keys of the selected rows. */
export function selectedRowKeys(g: GridAccess, rect: Rect | undefined): string[] {
  if (rect === undefined) return [];
  const out: string[] = [];
  for (let r = rect.top; r <= Math.min(rect.bottom, g.rows - 1); r++) out.push(g.rowKey(r));
  return out;
}
